/**
 * Set of utils that helps to identify any errors present on the layout that is submitted by library authors
 */
import {
  type ExpressionElement,
  type Expression,
  EElement,
  ECall,
  TEmptyRecord,
} from '@packages/systems/wfdl-ast';
import {
  Expressions,
  Component,
  Plugins,
  matchExpression,
  Variable,
  Types,
  getSystem,
} from '@packages/systems/wfdl';
import {
  qualify,
  getPluginName,
  QualifiedMap,
  parseQualified,
  identifier,
} from '@packages/systems/wfdl-Qualified';
import {getElementLabel, isUserComponent} from '@packages/utilities/platform';
import type {
  LayoutExport,
  LibraryExport,
  LibraryExportType,
  ErrorResult,
  TraversalError,
  TraversalErrorType,
} from './types';
import {LIBRARY_EXPORT_TYPES} from './constants';
import {reportBug} from '@packages/utilities/reporter';
import {pluralize} from '@packages/utilities/string-utils';
import {concatTo, constant, emptyArray} from '@packages/utilities/fp/utils';
import {getLibraryExportComponent} from './utils/getLibraryExportComponent';
import {isConditional} from '@packages/systems/plugins-utils/Conditionals';
import {getOverlayTargetReference} from '@packages/systems/overlays';
import {
  getSlotDefaultLists,
  hasSlotDefaultContent,
} from '@packages/utilities/platform-slots';
import {pair, type Pair} from '@packages/utilities/Pair';
import {parseCollectionListPropTypeName} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

/**
 * Configuration for what elements are allowed in a library.
 * These are base configs that can be extended based on specific needs.
 */
const BASE_ALLOWED_PLUGINS = [
  'Animation',
  'Basic',
  'Builtin',
  'Dropdown',
  'Embed',
  'Form',
  'Icon',
  'Layout',
  'Lightbox',
  'Navbar',
  'Search',
  'Slider',
  'Tabs',
  'Widget',
] as const;

/**
 * Plugin configuration for different library export types
 */
type PluginConfig = {
  allowedPlugins: readonly string[];
  bannedElements: readonly [string, string][];
};

/**
 * Base configuration that applies to all library export types
 */
const baseConfig: PluginConfig = {
  allowedPlugins: BASE_ALLOWED_PLUGINS,
  bannedElements: [
    ['Form', 'FormReCaptcha'],
    ['Widget', 'MapWidget'],
  ],
} as const;

/**
 * Configs specific to Layouts
 */
const layoutConfig: PluginConfig = {
  allowedPlugins: [
    // Add here any allowed plugins specific to Layouts
  ],
  bannedElements: [
    // Add here any banned elements specific to Layouts
  ],
} as const;

/**
 * Configs specific to Components
 */
const componentConfig: PluginConfig = {
  allowedPlugins: [
    // Add here any allowed plugins specific to Components
    'BackgroundVideo',
    'Boolean',
    'Code',
    '__SitePlugin',
    'List',
    'Modal',
    'Popover',
    'Text',
    'Visibility',
    // We don't want Marketplace Libraries to support Slots as
    // those are not persisted as components.
    'Slots',
    'Localization',
  ],
  bannedElements: [
    // Add here any banned elements specific to Components
  ],
} as const;

const FORBIDDEN_BINDING: TraversalError[] = [
  {
    type: 'FORBIDDEN_BINDING',
  },
];

/**
 * We don't gather any information about the offending bindings
 * just that there are some. That's why we can use the same
 * FORBIDDEN_BINDING error.
 */
const isBinding = (context: VerificationContext) =>
  pair({...context, parentContainsError: true}, FORBIDDEN_BINDING);

/**
 * Get the configuration for a specific library export type
 */
const getPluginConfig = (
  libraryExportType: LibraryExportType
): PluginConfig => {
  // Start with base config
  const config = {
    allowedPlugins: [...baseConfig.allowedPlugins],
    bannedElements: [...baseConfig.bannedElements],
  };

  // Add type-specific plugins
  if (libraryExportType === LIBRARY_EXPORT_TYPES.COMPONENT) {
    config.allowedPlugins = [
      ...config.allowedPlugins,
      ...componentConfig.allowedPlugins,
    ];
    config.bannedElements = [
      ...config.bannedElements,
      ...componentConfig.bannedElements,
    ];
  } else if (libraryExportType === LIBRARY_EXPORT_TYPES.LAYOUT) {
    config.allowedPlugins = [
      ...config.allowedPlugins,
      ...layoutConfig.allowedPlugins,
    ];
    config.bannedElements = [
      ...config.bannedElements,
      ...layoutConfig.bannedElements,
    ];
  }

  return config;
};

/**
 * We convert the configuration into a set to get O(1) look up time for
 * allowed plugins.
 */
const getPluginSets = (libraryExportType: LibraryExportType) => {
  const config = getPluginConfig(libraryExportType);
  return new Set(config.allowedPlugins.map(Plugins.name));
};

const reduceBannedElementsFn = (
  memo: QualifiedMap<Component.ComponentName, true>,
  [pluginName, componentName]: readonly [string, string]
) =>
  memo.set(
    qualify(Plugins.name(pluginName), Component.name(componentName)),
    true
  );

/**
 * The list of banned elements are converted to a qualified map for O(1)
 * look up time.
 */
const getBannedElements = (
  libraryExportType: LibraryExportType
): QualifiedMap<Component.ComponentName, true> => {
  const config = getPluginConfig(libraryExportType);
  return config.bannedElements.reduce<
    QualifiedMap<Component.ComponentName, true>
  >(reduceBannedElementsFn, QualifiedMap.empty());
};

/**
 * Context type for tracking whether we're inside a Conditional expression or
 * Popover or Modal target reference, and whether the parent contains already
 * an error. This allows us to skip processing of children and finding new
 * errors if the parent contains an error.
 */
type VerificationContext = {
  insideConditional: boolean;
  insideOverlayTargetReference: boolean;
  parentContainsError: boolean;
};

const initialContext: VerificationContext = {
  insideConditional: false,
  insideOverlayTargetReference: false,
  parentContainsError: false,
};

const overlayTargetContextVariable = Variable.unqualified(
  identifier('context')
);

/**
 * verifyExpression is called for each Expression AST node in the ComponentMap
 * It returns back the updated context which is used for sub AST nodes and any
 * errors it would like reported.
 */
const verifyExpression =
  (libraryExportType: LibraryExportType) =>
  (expr: Expression) =>
  (
    context: VerificationContext
  ): Pair<VerificationContext, TraversalError[]> => {
    // If parent contains error, skip processing this node and its children
    if (context.parentContainsError) {
      return pair(context, emptyArray);
    }

    // Get plugin sets based on current configuration
    const ALLOWED_PLUGINS = getPluginSets(libraryExportType);
    const BANNED_ELEMENTS = getBannedElements(libraryExportType);

    return matchExpression<Pair<VerificationContext, TraversalError[]>>({
      Call: (call) => {
        if (
          libraryExportType === LIBRARY_EXPORT_TYPES.COMPONENT &&
          getOverlayTargetReference(ECall(call))
        ) {
          return pair(
            {...context, insideOverlayTargetReference: true},
            emptyArray
          );
        }

        // Check if the current call is a conditional or contains a conditional argument
        if (
          !context.insideConditional &&
          (isConditional(ECall(call)) || isConditional(call.arg))
        ) {
          return pair({...context, insideConditional: true}, emptyArray);
        }
        return pair(context, emptyArray);
      },
      Element: (element: ExpressionElement) => {
        const isFromAllowedPlugin = ALLOWED_PLUGINS.has(
          getPluginName(element.type)
        );
        const isBannedElement = BANNED_ELEMENTS.has(element.type);
        // If its not an allowed element we set the parentContainsError flag since it will need to be removed
        // which would resolve children issues as a side-effect.
        if (!isFromAllowedPlugin || isBannedElement) {
          // Set parentContainsError to true to stop traversal since children of forbidden elements don't need to be checked
          return pair({...context, parentContainsError: true}, [
            {
              type: 'FORBIDDEN_ELEMENT',
              element,
            },
          ]);
        }
        const hasLegacyInteraction = Expressions.getIn(
          Expressions.getElementData(element),
          [{at: 'macro', in: 'Record'}]
        );
        if (hasLegacyInteraction) {
          // Don't stop traversal for legacy interactions - we want to find all of them
          return pair(context, [
            {
              type: 'LEGACY_INTERACTION',
              element,
            },
          ]);
        }
        return pair(context, emptyArray);
      },
      Variable: (variable: Variable.VariableType) => {
        // For Layouts we don't support props
        if (libraryExportType === LIBRARY_EXPORT_TYPES.LAYOUT) {
          return isBinding(context);
        }

        // Popover and Modal target references use an unqualified runtime
        // `context` variable. Keep traversing the call so any other binding is
        // still validated, but allow that exact variable within a recognized
        // call.
        if (
          context.insideOverlayTargetReference &&
          Variable.equals(overlayTargetContextVariable)(variable)
        ) {
          return pair(context, emptyArray);
        }

        // If we are inside a conditional, we allow all possible variables
        if (context.insideConditional) {
          return pair(context, emptyArray);
        }

        const variableName = Variable.getName(variable);

        // Current logic is to allow Component Properties by way of
        // `variableName === 'data` through and deny all other variable types
        // except those related to LocalesList elements and `page`
        // (PageBindings — resolves to the runtime page where the component
        // is rendered, so it's site-portable).
        if (['data', 'localesListItem', 'page'].includes(variableName)) {
          return pair(context, emptyArray);
        }

        // Now check if variable belongs to any of the allowed plugins,
        // in case it is a qualified name.
        const qualifiedVariableName = parseQualified(identifier)(variable);
        if (qualifiedVariableName) {
          const isFromAllowedPlugin = ALLOWED_PLUGINS.has(
            getPluginName(qualifiedVariableName)
          );
          if (isFromAllowedPlugin) {
            return pair(context, emptyArray);
          }
        }
        return isBinding(context);
      },
      _: constant(pair(context, emptyArray)),
    })(expr);
  };

/**
 * traverseExpression walks the entire Expression AST calling into verifyExpression for each node.
 * It does this with sub tree optimization, so changing a subtree only requires that subtree to be
 * traversed again, results from other parts of the tree can be reused.
 *
 * The other advantage is we're able to do all Element validations for the entire tree via a
 * single walk.
 */
const traverseExpression: (
  verifierForExpression: ExpressionVerifierWithContext
) => (arg1: Expression) => TraversalError[] = (verifierForExpression) => {
  return Expressions.everythingWithContext<VerificationContext>(initialContext)<
    TraversalError[]
  >(concatTo)(verifierForExpression);
};
type ExpressionVerifierWithContext = (
  expr: Expression
) => (
  context: VerificationContext
) => Pair<VerificationContext, TraversalError[]>;

/**
 * We currently render errors based on error type, not where in the tree
 * those errors were encountered. This is a type safe way of grouping
 * the errors.
 */
function groupErrorsByType(
  errors: TraversalError[]
): Partial<Record<TraversalError['type'], TraversalError[]>> {
  return errors.reduce<Record<string, TraversalError[]>>((memo, error) => {
    memo[error.type] = memo[error.type]
      ? (memo[error.type] as TraversalError[]).concat(error)
      : [error];
    return memo;
  }, {});
}

/**
 * Whether a component's `origin` marks it as belonging to another library. An
 * origin pointing at an uninstalled or superseded version still counts: it is
 * not the site's own resource either way.
 */
function hasCrossLibraryOrigin(
  origin: Component.ComponentType['origin']
): boolean {
  if (!origin) {
    return false;
  }

  // Basic origins are residue from a fixed bug, pending migration (SCALE-959).
  return getPluginName(origin) !== 'Basic';
}

function getSlotDefaultErrors({
  component,
  components,
  canShareSlotDefaults,
  verifierForExpression,
}: {
  component: Component.ComponentType;
  components: Component.ComponentMap;
  canShareSlotDefaults: boolean;
  verifierForExpression: ExpressionVerifierWithContext;
}): TraversalError[] {
  if (!canShareSlotDefaults) {
    return [{type: 'FORBIDDEN_SLOT_DEFAULT'}];
  }

  const fields = Types.getRecordFields(Component.getDataType(component)) ?? {};
  return getSlotDefaultLists(component).flatMap(([fieldId, list]) => {
    const elementErrors = traverseExpression(verifierForExpression)(list);
    const fieldType = fields[fieldId];
    const slotLabel = fieldType ? (getSystem(fieldType)?.label ?? '') : '';
    const danglingErrors = Object.values(Expressions.allElements(list)).flatMap(
      (element): TraversalError[] =>
        isUserComponent(element.type) && !components.has(element.type)
          ? [
              {
                type: 'DANGLING_SLOT_DEFAULT_REFERENCE',
                elementId: element.id,
                slotLabel,
              },
            ]
          : []
    );
    return [...elementErrors, ...danglingErrors];
  });
}

/**
 * Get the list of errors that are present on the layout that is submitted by library author
 * @param {LayoutExport} libraryExport
 * @param {Component.ComponentMap} components
 * @returns {ErrorResult[]} array of errors that are present on the layout
 */
export const getLibraryExportErrors = <T>({
  libraryExport,
  components,
  plugins,
  canShareSlotDefaults = false,
}: {
  libraryExport: LibraryExport<T>;
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  canShareSlotDefaults?: boolean;
}): ErrorResult[] => {
  const {rootElement, component} = getLibraryExportComponent({
    libraryExport,
    components,
  });

  if (!rootElement) {
    reportBug(
      'Invalid Library Export',
      "Library export doesn't contain any elements",
      {
        team: 'DXP',
        priority: 'warning',
        ticket: 'https://webflow.atlassian.net/browse/DESXP-5592',
        libraryExport,
      }
    );
    return [];
  }

  const dataType = component ? Component.getDataType(component) : TEmptyRecord;
  const recordFields = Types.getRecordFields(dataType) || {};
  const unsupportedDataTypes = Object.entries(recordFields).reduce<
    TraversalError[]
  >((res, [_, type]) => {
    const name = Types.getTypeConstructor(type);
    if (name) {
      const parsed = parseCollectionListPropTypeName(name);
      const system = getSystem(type);
      if (parsed) {
        res.push({
          type: 'FORBIDDEN_PROPERTY',
          label: system?.label || '',
        });
      }
    }
    return res;
  }, []);

  // Sharing one would leave the compiled plugin referencing a component it does
  // not contain. Marked invalid rather than dropped so that
  // `propagateComponentVerificationToParents` also invalidates any site
  // component whose definition nests one.
  const crossLibraryErrors: TraversalError[] =
    libraryExport.type === LIBRARY_EXPORT_TYPES.COMPONENT &&
    hasCrossLibraryOrigin(component?.origin)
      ? [{type: 'CROSS_LIBRARY_COMPONENT'}]
      : [];

  // Here is where we validate the export item
  const verifierForExpression = verifyExpression(libraryExport.type);
  const results = traverseExpression(verifierForExpression)(
    EElement(rootElement)
  );
  // Slot defaults live on defaultData, so the render traversal above misses them.
  const slotDefaultErrors: TraversalError[] =
    component && hasSlotDefaultContent(component)
      ? getSlotDefaultErrors({
          component,
          components,
          canShareSlotDefaults,
          verifierForExpression,
        })
      : [];
  const errorsByType: Partial<Record<TraversalErrorType, TraversalError[]>> =
    groupErrorsByType([
      ...results,
      ...unsupportedDataTypes,
      ...slotDefaultErrors,
      ...crossLibraryErrors,
    ]);
  // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type '("FORBIDDEN_ELEMENT" | "LEGACY_INTERACTION" | "FORBIDDEN_BINDING")[]'.
  const errorTypesFound: TraversalErrorType[] = Object.keys(errorsByType);

  // Here is where we take all the errors found for a layout and turn them into user friendly errors.
  const errors = errorTypesFound.reduce<Array<ErrorResult>>(
    (memo, errorType: TraversalErrorType) => {
      switch (errorType) {
        case 'FORBIDDEN_ELEMENT': {
          const forbiddenElementErrors = errorsByType[errorType];
          // @ts-expect-error - TS18048 - 'forbiddenElementErrors' is possibly 'undefined'.
          forbiddenElementErrors.forEach((forbiddenElementError) => {
            // This code only need to be run by the type checker to maintain Flow's type refinement.
            /*:: if (forbiddenElementError.type !== 'FORBIDDEN_ELEMENT') return [];  */

            // @ts-expect-error - TS2339 - Property 'element' does not exist on type 'TraversalError'.
            const forbiddenElement = forbiddenElementError.element;
            const elementLabel = getElementLabel(
              forbiddenElement,
              EElement(rootElement),
              components,
              plugins
            );

            memo.push({
              message: `Cannot include ${elementLabel}`,
              elementId: forbiddenElement.id,
              elementLabel,
              type: errorType,
            });
          });
          return memo;
        }
        case 'LEGACY_INTERACTION': {
          const interactionErrors = errorsByType[errorType];
          const interactionsCount = interactionErrors
            ? interactionErrors.length
            : 0;

          memo.push({
            message: `Cannot contain legacy ${pluralize(
              interactionsCount,
              'interaction'
            )}${interactionsCount > 1 ? ` (${interactionsCount})` : ''}`,
            type: errorType,
          });
          return memo;
        }
        case 'FORBIDDEN_BINDING': {
          const bindingErrors = errorsByType[errorType];
          const bindingsCount = bindingErrors ? bindingErrors.length : 0;

          memo.push({
            message: `Cannot contain property ${pluralize(bindingsCount, 'binding')}${
              bindingsCount > 1 ? ` (${bindingsCount})` : ''
            }`,
            type: errorType,
          });
          return memo;
        }
        case 'FORBIDDEN_PROPERTY': {
          const forbiddenPropertyErrors = errorsByType[errorType];
          if (!forbiddenPropertyErrors) {
            return memo;
          }
          forbiddenPropertyErrors.forEach((forbiddenPropertyError) => {
            if (forbiddenPropertyError.type !== 'FORBIDDEN_PROPERTY') {
              return;
            }
            const label = forbiddenPropertyError.label;

            memo.push({
              message: `Cannot include property${label ? ` ${label}` : ''}`,
              type: errorType,
            });
          });
          return memo;
        }
        case 'FORBIDDEN_SLOT_DEFAULT': {
          memo.push({
            message: 'Cannot contain slot default content',
            type: errorType,
          });
          return memo;
        }
        case 'DANGLING_SLOT_DEFAULT_REFERENCE': {
          (errorsByType[errorType] ?? []).forEach((danglingError) => {
            if (danglingError.type !== 'DANGLING_SLOT_DEFAULT_REFERENCE') {
              return;
            }
            memo.push({
              message: `Slot default content references a deleted component${
                danglingError.slotLabel ? ` in ${danglingError.slotLabel}` : ''
              }`,
              elementId: danglingError.elementId,
              type: errorType,
            });
          });
          return memo;
        }
        case 'CROSS_LIBRARY_COMPONENT': {
          memo.push({
            message: 'Cannot include a component from an installed library',
            type: errorType,
          });
          return memo;
        }
        default:
          // Ensure all errors have a user friendly message with type exhaustion.
          /*:: (errorType: empty); */
          return memo;
      }
    },
    []
  );

  return errors;
};

/**
 * Describes the Layout type used by the publishing dialog.
 * This is expected to be the `LayoutExport` of type `Layout` as
 * stored in `DesignerStore.localLibrary` and if there are any disallowed
 * elements or elements properties in the layout errors should be populated.
 */
export type Layout<T> =
  | LayoutExport<T>
  | (LayoutExport<T> & {
      errors: string[];
    });

/**
 * Groups layouts based on whether they are valid or invalid for publishing
 */
export type GroupedLayouts<T> = GroupedLibraryExports<LayoutExport<T>>;

type GroupedLibraryExports<LibraryExportT> = {
  valid: LibraryExportT[];
  invalid: (LibraryExportT & {
    errors: ErrorResult[];
  })[];
};

/**
 * Groups layouts based on whether they are valid or invalid for publishing
 */
export const verifyLibraryExports = <T extends LibraryExport<unknown>>(
  libraryExports: Array<T>,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  canShareSlotDefaults?: boolean
): GroupedLibraryExports<T> => {
  return libraryExports.reduce(
    (res, libraryExport) => {
      let isValid = true;
      // Variables, Assets, TagStyles, Fonts, and SourceFiles don't need to worry about
      // this — the compiler gate only applies to renderable export types
      // (components and layouts).
      if (
        libraryExport.type === LIBRARY_EXPORT_TYPES.VARIABLE ||
        libraryExport.type === LIBRARY_EXPORT_TYPES.ASSET ||
        libraryExport.type === LIBRARY_EXPORT_TYPES.TAG_STYLE ||
        libraryExport.type === LIBRARY_EXPORT_TYPES.FONT ||
        libraryExport.type === LIBRARY_EXPORT_TYPES.SOURCE_FILE
      ) {
        res.valid.push(libraryExport);
        return res;
      }
      // Layout specific errors
      if (libraryExport.type === LIBRARY_EXPORT_TYPES.LAYOUT) {
        isValid =
          isValid &&
          Boolean(libraryExport.layoutMetadata.category.length) &&
          Boolean(libraryExport.layoutMetadata.previewImage.url);
      }

      const errors = getLibraryExportErrors({
        libraryExport,
        components,
        plugins,
        canShareSlotDefaults,
      });

      if (!isValid || errors.length) {
        res.invalid.push({...libraryExport, errors});
      } else {
        res.valid.push(libraryExport);
      }

      return res;
    },
    {
      valid: [],
      invalid: [],
    } as GroupedLibraryExports<T>
  );
};
